// ── QR modal ────────────────────────────────────────────────────────
// Toolbar button that opens a full-viewport dialog showing a QR code for
// this site. The code itself is a static SVG in assets/ rather than
// something we generate at runtime: the URL never changes, so shipping a
// pre-made vector is smaller, faster, and has no third-party dependency.
//
// The dialog is a native <dialog> driven with showModal(), which buys us
// top-layer stacking (no z-index negotiation with the SVG field or the
// chaos overlays), a real focus trap, focus restore on close, and
// Escape-to-dismiss — all from the platform. There is no close button;
// Escape or a click anywhere off the code dismisses it, the latter
// implemented below by checking that the click landed on the <dialog>
// box itself rather than on the code or the contrast toggle.

// ── Screen wake lock ────────────────────────────────────────────────
// There is no web API for setting screen brightness — the old Screen
// Brightness proposal never shipped in any engine, and nothing replaced
// it. The Screen Wake Lock API is the closest real capability: it can't
// turn the backlight up, but it stops the phone from dimming and then
// sleeping while the code is on screen, which is the failure that
// actually ruins a scan. Chrome/Edge 84+, Safari 16.4+, Firefox 126+;
// secure contexts only, so it's inert on plain-http local previews.
//
// The lock is released by the UA whenever the document stops being
// visible, so re-acquiring on `visibilitychange` is required rather than
// belt-and-braces: without it, a glance at another app permanently drops
// the lock for the rest of the session.
let wakeLock = null;

async function acquireWakeLock() {
  if (!('wakeLock' in navigator) || wakeLock) return;
  try {
    wakeLock = await navigator.wakeLock.request('screen');
    wakeLock.addEventListener('release', () => { wakeLock = null; });
  } catch (err) {
    // Rejects on insecure contexts, hidden documents, and when the OS
    // declines (battery saver). Non-fatal — the code still displays.
    console.warn('Screen wake lock unavailable:', err);
    wakeLock = null;
  }
}

async function releaseWakeLock() {
  if (!wakeLock) return;
  try {
    await wakeLock.release();
  } catch (err) {
    console.warn('Releasing screen wake lock failed:', err);
  }
  wakeLock = null;
}

export function setupQrModal() {
  const btn    = document.getElementById('qr-btn');
  const dialog = document.getElementById('qr-modal');
  if (!btn || !dialog) return;

  const contrastBtn = document.getElementById('qr-contrast');
  const image       = dialog.querySelector('.qr-image');

  // Very old engines without <dialog> support: showModal is undefined.
  // Fall back to the plain `open` attribute — no backdrop and no focus
  // trap, but the content is still reachable rather than dead.
  const supportsModal = typeof dialog.showModal === 'function';

  const setOpenState = (open) => {
    btn.dataset.active = open ? 'true' : 'false';
  };

  const open = () => {
    if (dialog.open) return;
    if (supportsModal) dialog.showModal();
    else dialog.setAttribute('open', '');
    setOpenState(true);
    acquireWakeLock();
  };

  const close = () => {
    if (!dialog.open) return;
    if (supportsModal) dialog.close();
    else dialog.removeAttribute('open');
    setOpenState(false);
    releaseWakeLock();
  };

  // The UA drops the lock whenever the document is hidden; take it back
  // if the dialog is still up when the visitor returns.
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden && dialog.open) acquireWakeLock();
  });

  // Stop pointerdown from bubbling so pressing the button during Fun
  // Mode's `awaiting` state doesn't register as the gravity-well press
  // (same guard the email and mute buttons use).
  btn.addEventListener('pointerdown', (e) => { e.stopPropagation(); });
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    if (dialog.open) close(); else open();
  });

  // Click-off-to-close. Because the dialog fills the viewport, a click
  // on the empty area around the code reports the <dialog> element
  // itself as the target; clicks on the code or the toggle have a
  // descendant as their target and are left alone. Keeping the code
  // itself non-dismissing matters — you're often holding the screen up
  // for someone else to scan.
  //
  // stopPropagation on every click inside the dialog keeps the window-
  // level click handler (which fires a pulse into the node graph) from
  // running while a full-screen overlay is up.
  dialog.addEventListener('click', (e) => {
    e.stopPropagation();
    if (e.target === dialog) close();
  });
  dialog.addEventListener('pointerdown', (e) => { e.stopPropagation(); });

  // Escape triggers `cancel` then `close`; the `close` event also fires
  // for programmatic close() and for a form[method=dialog] submit, so
  // syncing button state here covers every dismissal path at once.
  dialog.addEventListener('close', () => setOpenState(false));

  // High-contrast toggle: drives the code to true black on true white
  // and blanks the surround to match. The state is deliberately sticky
  // across opens within a session — someone who needed it once while
  // showing the code around almost certainly needs it again.
  contrastBtn?.addEventListener('click', () => {
    const on = dialog.dataset.contrast !== 'true';
    dialog.dataset.contrast = on ? 'true' : 'false';
    contrastBtn.setAttribute('aria-pressed', on ? 'true' : 'false');
  });

  // If the SVG is missing or fails to decode, say so rather than leaving
  // a broken-image icon sitting in an otherwise empty overlay.
  image?.addEventListener('error', () => {
    console.warn('QR image failed to load:', image.getAttribute('src'));
    image.replaceWith(Object.assign(document.createElement('p'), {
      className: 'qr-fallback',
      textContent: 'acx.ci',
    }));
  });
}
